/// <reference types="vite/client" />
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { _registerLocaleLoader } from './language.js';
import { withPluralFallbacks } from './plurals.js';
// ============================================================================
// English Translations (Base Language — always bundled)
// ============================================================================
import commonEn from './locales/en/common.json' with { type: 'json' };
import notificationsEn from './locales/en/notifications.json' with { type: 'json' };
import settingsEn from './locales/en/settings.json' with { type: 'json' };
import navEn from './locales/en/nav.json' with { type: 'json' };
import pagesEn from './locales/en/pages.json' with { type: 'json' };
import mobileEn from './locales/en/mobile.json' with { type: 'json' };
export const defaultNS = 'common';
export const namespaces = [
    'common',
    'notifications',
    'settings',
    'nav',
    'pages',
    'mobile',
];
// English is always available statically
export const resources = {
    en: {
        common: commonEn,
        notifications: notificationsEn,
        settings: settingsEn,
        nav: navEn,
        pages: pagesEn,
        mobile: mobileEn,
    },
};
export const supportedLanguages = [
    'en',
    'af-ZA',
    'ar-SA',
    'ca-ES',
    'cs-CZ',
    'da-DK',
    'de-DE',
    'el-GR',
    'es-ES',
    'fi-FI',
    'fr-FR',
    'he-IL',
    'hu-HU',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'nl-NL',
    'no-NO',
    'pl-PL',
    'pt-BR',
    'pt-PT',
    'ro-RO',
    'ru-RU',
    'sr-SP',
    'sv-SE',
    'tr-TR',
    'uk-UA',
    'vi-VN',
    'zh-CN',
    'zh-TW',
];
const localeModules = import.meta.glob('./locales/*-*/*.json');
const localeLoaders = Object.fromEntries(supportedLanguages
    .filter((l) => l !== 'en')
    .map((locale) => [
    locale,
    async () => {
        const bundles = {};
        await Promise.all(namespaces.map(async (ns) => {
            const path = `./locales/${locale}/${ns}.json`;
            const loader = localeModules[path];
            if (loader) {
                const mod = await loader();
                bundles[ns] = mod.default;
            }
        }));
        return bundles;
    },
]));
// ============================================================================
// Locale Loading
// ============================================================================
const loadedLocales = new Set(['en']);
/**
 * Load a locale's translation data on-demand.
 * English is always available (statically bundled). Other locales are fetched
 * as separate chunks and registered with i18next via addResourceBundle.
 */
export async function loadLocale(lang) {
    if (loadedLocales.has(lang))
        return;
    const loader = localeLoaders[lang];
    if (!loader)
        return;
    try {
        const bundles = await loader();
        for (const [ns, data] of Object.entries(bundles)) {
            i18n.addResourceBundle(lang, ns, withPluralFallbacks(lang, data), true, true);
        }
        loadedLocales.add(lang);
    }
    catch (err) {
        console.error(`[i18n] Failed to load locale "${lang}", falling back to English`, err);
    }
}
// Register the Vite-based locale loader so language.ts can use it
_registerLocaleLoader(loadLocale);
// ============================================================================
// i18next Configuration
// ============================================================================
export const defaultI18nConfig = {
    lng: 'en',
    fallbackLng: 'en',
    defaultNS,
    ns: namespaces,
    resources,
    interpolation: {
        escapeValue: false, // React already escapes values
    },
    // Returning the key is better than empty string for debugging
    returnNull: false,
    returnEmptyString: false,
};
// Prevents race conditions when initI18n is called concurrently
let initPromise = null;
export async function initI18n(options) {
    if (i18n.isInitialized) {
        // If already initialized but a different locale is requested, load it
        const targetLng = options?.lng || 'en';
        if (targetLng !== 'en') {
            await loadLocale(targetLng);
        }
        return i18n;
    }
    if (initPromise) {
        return initPromise;
    }
    const targetLng = options?.lng || 'en';
    initPromise = i18n
        .use(initReactI18next)
        .init({
        ...defaultI18nConfig,
        ...options,
    })
        .then(async () => {
        // Load non-English locale data after init
        if (targetLng !== 'en') {
            await loadLocale(targetLng);
        }
        initPromise = null;
        return i18n;
    })
        .catch((error) => {
        initPromise = null;
        throw error;
    });
    return initPromise;
}
export { i18n };
//# sourceMappingURL=config.js.map